import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Select from 'react-select';
import { CKEditor } from '@ckeditor/ckeditor5-react';
import ClassicEditor from '@ckeditor/ckeditor5-build-classic';
import { useGetArticleQuery, useGetAllTagsQuery, useCreateArticleMutation, useUpdateArticleMutation } from '../../../store/api/adminApi';
import PageLoader from '../../../components/PageLoader';
import ImageUpload from '../../../components/ImageUpload';
import { showSuccess, showError } from '../../../utils/toast';
import { useLanguage } from '../../../context/LanguageContext';

const AdminArticleForm: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const { id } = useParams<{ id: string }>();
  const isEdit = !!id;
  
  const { data, isLoading } = useGetArticleQuery(id!, { skip: !isEdit });
  const { data: tagsEnData } = useGetAllTagsQuery('en');
  const { data: tagsArData } = useGetAllTagsQuery('ar');
  const [createArticle, { isLoading: isCreating }] = useCreateArticleMutation();
  const [updateArticle, { isLoading: isUpdating }] = useUpdateArticleMutation();

  const [formData, setFormData] = useState({
    title: '',
    title_ar: '',
    content: '',
    content_ar: '',
    tags: [] as number[],
    tags_ar: [] as number[],
    image: null as File | null,
  });

  const [existingImage, setExistingImage] = useState<string | undefined>(undefined);

  useEffect(() => {
    if (isEdit && data?.data) {
      const article = data.data;
      // Separate tags by language
      const tagsEn: number[] = [];
      const tagsAr: number[] = [];
      
      if (article.tags && Array.isArray(article.tags)) {
        article.tags.forEach((tag: any) => {
          if (tag.lang === 'en' || tag.tag?.lang === 'en') {
            tagsEn.push(tag.tag_id || tag.id || tag.tag?.id);
          } else if (tag.lang === 'ar' || tag.tag?.lang === 'ar') {
            tagsAr.push(tag.tag_id || tag.id || tag.tag?.id);
          }
        });
      }
      
      setFormData({
        title: article.title || '',
        title_ar: article.title_ar || '',
        content: article.content || article.description || '',
        content_ar: article.content_ar || article.description_ar || '',
        tags: tagsEn,
        tags_ar: tagsAr,
        image: null,
      });
      // Handle both single image and images array
      if (article.image) {
        // Extract filename from full URL if needed
        const imageFilename = article.image.includes('/uploads/') 
          ? article.image.split('/uploads/')[1] 
          : article.image;
        setExistingImage(imageFilename);
      } else if (article.images && Array.isArray(article.images) && article.images.length > 0) {
        const imageFilename = article.images[0].includes('/uploads/') 
          ? article.images[0].split('/uploads/')[1] 
          : article.images[0];
        setExistingImage(imageFilename);
      } else {
        setExistingImage(undefined);
      }
    }
  }, [isEdit, data]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleContentChange = (content: string) => {
    setFormData(prev => ({ ...prev, content }));
  };

  const handleContentArChange = (content_ar: string) => {
    setFormData(prev => ({ ...prev, content_ar }));
  };

  const handleTagsChange = (selectedOptions: any) => {
    const tagIds = selectedOptions ? selectedOptions.map((option: any) => option.value) : [];
    setFormData(prev => ({ ...prev, tags: tagIds }));
  };

  const handleTagsArChange = (selectedOptions: any) => {
    const tagIds = selectedOptions ? selectedOptions.map((option: any) => option.value) : [];
    setFormData(prev => ({ ...prev, tags_ar: tagIds }));
  };

  const handleImageChange = (file: File | null) => {
    setFormData(prev => ({ ...prev, image: file }));
  };

  const handleRemoveExistingImage = () => {
    setExistingImage(undefined);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      const submitData = new FormData();
      submitData.append('title', formData.title);
      submitData.append('title_ar', formData.title_ar);
      submitData.append('content', formData.content);
      submitData.append('content_ar', formData.content_ar);
      
      // Append tags arrays
      formData.tags.forEach((tagId) => {
        submitData.append('tags[]', tagId.toString());
      });
      formData.tags_ar.forEach((tagId) => {
        submitData.append('tags_ar[]', tagId.toString());
      });
      
      // Append image (for articles, API expects single image)
      if (formData.image) {
        submitData.append('image', formData.image);
        submitData.append('images[]', formData.image); // Also append as images[] for compatibility
      }

      if (isEdit) {
        const result = await updateArticle({ id: parseInt(id!), data: submitData }).unwrap();
        showSuccess(result.message || t('Article updated successfully!'));
      } else {
        const result = await createArticle(submitData).unwrap();
        showSuccess(result.message || t('Article created successfully!'));
      }
      
      navigate('/admin/articles');
    } catch (err: any) {
      showError(err?.data?.message || t(isEdit ? 'Failed to update article. Please try again.' : 'Failed to create article. Please try again.'));
    }
  };

  if (isLoading) {
    return <PageLoader />;
  }

  const tagsEn = tagsEnData?.data || [];
  const tagsAr = tagsArData?.data || [];
  
  // Format tags for react-select
  const tagsEnOptions = tagsEn.map((tag: any) => ({
    value: tag.id,
    label: tag.tag || tag.name,
  }));
  
  const tagsArOptions = tagsAr.map((tag: any) => ({
    value: tag.id,
    label: tag.tag || tag.name,
  }));
  
  // Get selected tags for react-select
  const selectedTagsEn = tagsEnOptions.filter((option: any) => 
    formData.tags.includes(option.value)
  );
  
  const selectedTagsAr = tagsArOptions.filter((option: any) => 
    formData.tags_ar.includes(option.value)
  );

  return (
    <div className="app-main flex-column flex-row-fluid" id="kt_app_main">
      <div className="d-flex flex-column flex-column-fluid">
        <div id="kt_app_toolbar" className="app-toolbar py-3 py-lg-6">
          <div id="kt_app_toolbar_container" className="app-container container-xxl d-flex flex-stack">
            <div className="page-title d-flex flex-column justify-content-center flex-wrap me-3">
              <h1 className="page-heading d-flex text-dark fw-bold fs-3 flex-column justify-content-center my-0">
                {isEdit ? t('Edit Article') : t('Add New Article')}
              </h1>
              <ul className="breadcrumb breadcrumb-separatorless fw-semibold fs-7 my-0 pt-1">
                <li className="breadcrumb-item text-muted">
                  <a href="#" onClick={(e) => { e.preventDefault(); navigate('/admin/dashboard'); }} className="text-muted text-hover-primary">{t('Dashboard')}</a>
                </li>
                <li className="breadcrumb-item">
                  <span className="bullet bg-gray-400 w-5px h-2px"></span>
                </li>
                <li className="breadcrumb-item text-muted">
                  <a href="#" onClick={(e) => { e.preventDefault(); navigate('/admin/articles'); }} className="text-muted text-hover-primary">{t('Articles')}</a>
                </li>
              </ul>
            </div>
          </div>
        </div>
        
        <div id="kt_app_content" className="app-content flex-column-fluid">
          <div id="kt_app_content_container" className="app-container container-xxl">
            <div className="card">
              <div className="card-body pt-0">
                <form onSubmit={handleSubmit} id="kt_modal_new_target_form" className="form fv-plugins-bootstrap5 fv-plugins-framework">
                  <div className="row g-9 my-3">
                    <div className="col-md-6 fv-row fv-plugins-icon-container">
                      <label className="required fs-6 fw-semibold mb-2">{t('Title')}</label>
                      <input
                        type="text"
                        className="form-control form-control-solid"
                        placeholder={t('Enter Article Title')}
                        name="title"
                        value={formData.title}
                        onChange={handleInputChange}
                        required
                      />
                    </div>
                    
                    <div className="col-md-6 fv-row fv-plugins-icon-container">
                      <label className="fs-6 fw-semibold mb-2">{t('Title Arabic')}</label>
                      <input
                        type="text"
                        className="form-control form-control-solid"
                        placeholder={t('Enter Article Title in Arabic')}
                        name="title_ar"
                        value={formData.title_ar}
                        onChange={handleInputChange}
                      />
                    </div>
                    
                    <div className="col-md-12 fv-row fv-plugins-icon-container">
                      <label className="required fs-6 fw-semibold mb-2">{t('Content')}</label>
                      <CKEditor
                        editor={ClassicEditor}
                        data={formData.content}
                        onChange={(event, editor) => {
                          const data = editor.getData();
                          handleContentChange(data);
                        }}
                        config={{
                          toolbar: ['heading', '|', 'bold', 'italic', 'link', 'bulletedList', 'numberedList', '|', 'blockQuote', 'insertTable', '|', 'undo', 'redo'],
                        }}
                      />
                    </div>
                    
                    <div className="col-md-12 fv-row fv-plugins-icon-container">
                      <label className="fs-6 fw-semibold mb-2">{t('Content Arabic')}</label>
                      <CKEditor
                        editor={ClassicEditor}
                        data={formData.content_ar}
                        onChange={(event, editor) => {
                          const data = editor.getData();
                          handleContentArChange(data);
                        }}
                        config={{
                          toolbar: ['heading', '|', 'bold', 'italic', 'link', 'bulletedList', 'numberedList', '|', 'blockQuote', 'insertTable', '|', 'undo', 'redo'],
                          language: 'ar',
                        }}
                      />
                    </div>
                    
                    <div className="col-md-6 fv-row fv-plugins-icon-container">
                      <label className="fs-6 fw-semibold mb-2">{t('Tags')}</label>
                      <Select
                        isMulti
                        isSearchable
                        options={tagsEnOptions}
                        value={selectedTagsEn}
                        onChange={handleTagsChange}
                        placeholder={t('Select Tags')}
                        className="react-select-container"
                        classNamePrefix="react-select"
                        styles={{
                          control: (base) => ({
                            ...base,
                            minHeight: '48px',
                            borderColor: '#e4e6ef',
                            '&:hover': {
                              borderColor: '#e4e6ef',
                            },
                          }),
                          multiValue: (base) => ({
                            ...base,
                            backgroundColor: '#f1faff',
                          }),
                          multiValueLabel: (base) => ({
                            ...base,
                            color: '#009ef7',
                          }),
                          multiValueRemove: (base) => ({
                            ...base,
                            color: '#009ef7',
                            '&:hover': {
                              backgroundColor: '#009ef7',
                              color: 'white',
                            },
                          }),
                        }}
                      />
                    </div>
                    
                    <div className="col-md-6 fv-row fv-plugins-icon-container">
                      <label className="fs-6 fw-semibold mb-2">{t('Tags Arabic')}</label>
                      <Select
                        isMulti
                        isSearchable
                        options={tagsArOptions}
                        value={selectedTagsAr}
                        onChange={handleTagsArChange}
                        placeholder={t('Select Tags Arabic')}
                        className="react-select-container"
                        classNamePrefix="react-select"
                        styles={{
                          control: (base) => ({
                            ...base,
                            minHeight: '48px',
                            borderColor: '#e4e6ef',
                            '&:hover': {
                              borderColor: '#e4e6ef',
                            },
                          }),
                          multiValue: (base) => ({
                            ...base,
                            backgroundColor: '#f1faff',
                          }),
                          multiValueLabel: (base) => ({
                            ...base,
                            color: '#009ef7',
                          }),
                          multiValueRemove: (base) => ({
                            ...base,
                            color: '#009ef7',
                            '&:hover': {
                              backgroundColor: '#009ef7',
                              color: 'white',
                            },
                          }),
                        }}
                      />
                    </div>
                    
                    <div className="col-md-12 fv-row fv-plugins-icon-container">
                      <ImageUpload
                        existingImage={existingImage}
                        onImageChange={handleImageChange}
                        onRemoveExisting={handleRemoveExistingImage}
                        label={t('Upload Article Image')}
                        name="image"
                      />
                    </div>
                  </div>
                  
                  <div className="text-center">
                    <button type="button" id="kt_modal_new_target_cancel" className="btn btn-light me-3" onClick={() => navigate('/admin/articles')}>
                      {t('Discard')}
                    </button>
                    <button type="submit" id="kt_modal_new_target_submit" className="btn btn-base" disabled={isCreating || isUpdating}>
                      <span className="indicator-label">{t('Submit')}</span>
                      {(isCreating || isUpdating) && (
                        <span className="indicator-progress">
                          {t('Please wait...')}
                          <span className="spinner-border spinner-border-sm align-middle ms-2"></span>
                        </span>
                      )}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminArticleForm;
